import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Table,
  InputNumber,
  Typography,
  Space,
  Tag,
  message,
  Spin,
  Row,
  Col,
  Input,
  Popconfirm,
  Select,
  DatePicker
} from 'antd';
import dayjs from 'dayjs';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SendOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { pendingInvoicesApi } from '../services/api';

const { Title, Text } = Typography;

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  accountCode: string;
  taxType: string;
  selected: boolean;
  source: 'goods' | 'shipping' | 'manual';
}

interface PendingInvoiceDetails {
  id: string;
  shipmentId: string;
  shipmentRef: string;
  contactId: string;
  contactName: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  reference: string | null;
  lineItems: LineItem[];
  totalAmount: number;
  selectedTotalAmount: number;
  status: 'PENDING' | 'IN_REVIEW' | 'SENT' | 'FAILED';
  notes?: string;
  currency?: string;
  currencySymbol?: string;
  xeroInvoiceId?: string | null;
}

const ACCOUNT_OPTIONS = [
  { value: '4000', label: '4000 - Sales' },
  { value: '4100', label: '4100 - Shipping Revenue' },
  { value: '4200', label: '4200 - Commission Income' },
  { value: '200', label: '200 - Sales' },
];

const PendingInvoiceEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [lineItems, setLineItems] = useState<any[]>([]);
  const [invoiceDate, setInvoiceDate] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');

  const { data: invoice, isLoading, refetch } = useQuery<PendingInvoiceDetails | null>({
    queryKey: ['pending-invoice', id],
    queryFn: async () => {
      if (!id) return null;
      return await pendingInvoicesApi.getById(id) as unknown as PendingInvoiceDetails;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (invoice) {
      if (invoice.lineItems) {
        setLineItems(invoice.lineItems.map((item: LineItem) => ({
          ...item,
          key: item.id,
        })));
      }
      if (invoice.invoiceDate) setInvoiceDate(invoice.invoiceDate);
      if (invoice.dueDate) setDueDate(invoice.dueDate);
    }
  }, [invoice]);

  const updateLineItemsMutation = useMutation({
    mutationFn: async (items: any[]) => {
      if (!id) return;
      return await pendingInvoicesApi.updateLineItems(id, items);
    },
    onSuccess: () => {
      message.success('Line items updated successfully');
      queryClient.invalidateQueries({ queryKey: ['pending-invoice', id] });
    },
    onError: () => {
      message.error('Failed to update line items');
    },
  });

  const sendToXeroMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      return await pendingInvoicesApi.sendToXero(id);
    },
    onSuccess: (data: any) => {
      message.success('Invoice sent to Xero successfully');
      if (data?.xeroInvoiceId) {
        window.open(`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${data.xeroInvoiceId}`, '_blank');
      }
      navigate('/pending-invoices');
    },
    onError: (error: any) => {
      message.error(`Failed to send to Xero: ${error.message || 'Unknown error'}`);
    },
  });

  const addLineItemMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      return await pendingInvoicesApi.addLineItem(id, {
        description: 'New line item',
        quantity: 1,
        unitPrice: 0,
        accountCode: '4000',
        taxType: 'NONE',
        selected: true,
        source: 'manual',
      });
    },
    onSuccess: () => {
      message.success('Line item added');
      refetch();
    },
    onError: () => {
      message.error('Failed to add line item');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      return await pendingInvoicesApi.delete(id);
    },
    onSuccess: () => {
      message.success('Pending invoice deleted');
      navigate(-1);
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.error || 'Failed to delete pending invoice');
    },
  });

  const handleLineItemChange = (recordId: string, field: string, value: any) => {
    const newLineItems = [...lineItems];
    const index = newLineItems.findIndex(item => item.id === recordId);
    if (index > -1) {
      newLineItems[index] = { ...newLineItems[index], [field]: value };
      newLineItems[index].total = (newLineItems[index].quantity || 0) * (newLineItems[index].unitPrice || 0);
      setLineItems(newLineItems);
    }
  };

  const updateMetadataMutation = useMutation({
    mutationFn: async (data: { invoiceDate?: string; dueDate?: string }) => {
      if (!id) return;
      return await pendingInvoicesApi.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-invoice', id] });
    },
  });

  const handleSave = () => {
    updateLineItemsMutation.mutate(lineItems);
    
    if (invoice && (invoiceDate !== invoice.invoiceDate || dueDate !== invoice.dueDate)) {
      updateMetadataMutation.mutate({ invoiceDate, dueDate });
    }
  };

  const handleDelete = async (lineItemId: string) => {
    if (!id) return;
    try {
      await pendingInvoicesApi.deleteLineItem(id, lineItemId);
      message.success('Line item deleted');
      refetch();
    } catch (error) {
      message.error('Failed to delete line item');
    }
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unitPrice || 0)), 0);
  };

  const columns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: any) => (
        <Input.TextArea 
          value={text} 
          onChange={(e) => handleLineItemChange(record.id, 'description', e.target.value)}
          autoSize={{ minRows: 1 }}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (val: number, record: any) => (
        <InputNumber
          min={0}
          value={val}
          onChange={(value) => handleLineItemChange(record.id, 'quantity', value)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 150,
      render: (val: number, record: any) => (
        <InputNumber
          min={0}
          precision={2}
          prefix={invoice?.currencySymbol || '$'}
          value={val}
          onChange={(value) => handleLineItemChange(record.id, 'unitPrice', value)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 150,
      render: (val: number, record: any) => (
        <Text strong>
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: invoice?.currency || 'USD'
          }).format((record.quantity || 0) * (record.unitPrice || 0))}
        </Text>
      ),
    },
    {
      title: 'Account',
      dataIndex: 'accountCode',
      key: 'accountCode',
      width: 250,
      render: (text: string, record: any) => (
        <Select
          value={text}
          onChange={(value) => handleLineItemChange(record.id, 'accountCode', value)}
          options={ACCOUNT_OPTIONS}
          style={{ width: '100%' }}
          placeholder="Select Account"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this item?"
          onConfirm={() => handleDelete(record.id)}
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  if (isLoading || !invoice) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate(`/contracts/${invoice.shipmentRef}`)}
        style={{ marginBottom: 16 }}
      >
        Back to {invoice.shipmentRef}
      </Button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            Invoice #{invoice.invoiceNumber}
          </Title>
          <Space>
            <Tag color="geekblue">{invoice.shipmentRef}</Tag>
            <Tag color={
              invoice.status === 'PENDING' ? 'gold' : 
              invoice.status === 'IN_REVIEW' ? 'blue' : 
              invoice.status === 'SENT' ? 'green' : 'red'
            }>
              {invoice.status}
            </Tag>
          </Space>
        </div>
        <Space>
          {invoice.status !== 'SENT' && (
            <Popconfirm
              title="Delete Pending Invoice"
              description="Are you sure you want to delete this pending invoice?"
              onConfirm={() => deleteMutation.mutate()}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button 
                danger
                icon={<DeleteOutlined />}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
          <Button 
            icon={<SaveOutlined />} 
            onClick={handleSave}
            loading={updateLineItemsMutation.isPending}
          >
            Save Changes
          </Button>
          <Button 
            type="primary" 
            icon={<SendOutlined />}
            onClick={() => sendToXeroMutation.mutate()}
            loading={sendToXeroMutation.isPending}
            disabled={invoice.status === 'SENT'}
          >
            Send to Xero
          </Button>
        </Space>
      </div>

      {invoice.status === 'SENT' && invoice.xeroInvoiceId && (
        <Card style={{ marginBottom: 24, background: '#f6ffed', borderColor: '#b7eb8f' }}>
          <Space>
            <Tag color="green">SENT TO XERO</Tag>
            <Button 
              type="link" 
              href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${invoice.xeroInvoiceId}`}
              target="_blank"
            >
              View Invoice in Xero →
            </Button>
          </Space>
        </Card>
      )}

      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card 
            title="Line Items" 
            bodyStyle={{ padding: 0 }}
            extra={
              invoice.status !== 'SENT' && (
                <Button 
                  type="dashed" 
                  icon={<PlusOutlined />}
                  onClick={() => addLineItemMutation.mutate()}
                  loading={addLineItemMutation.isPending}
                >
                  Add Line Item
                </Button>
              )
            }
          >
            <Table
              dataSource={lineItems}
              columns={columns}
              pagination={false}
              rowKey="id"
              footer={() => (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <Text strong style={{ marginRight: 16, fontSize: 16 }}>
                    Total:
                  </Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: invoice.currency || 'USD'
                    }).format(calculateTotal())}
                  </Title>
                </div>
              )}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="Details">
            <Row gutter={24}>
              <Col span={6}>
                <Text type="secondary">Contact</Text>
                <div><Text strong>{invoice.contactName}</Text></div>
              </Col>
              
              <Col span={5}>
                <Text type="secondary">Invoice Date</Text>
                <div style={{ marginTop: 4 }}>
                  <DatePicker
                    value={invoiceDate ? dayjs(invoiceDate) : null}
                    onChange={(date) => setInvoiceDate(date ? date.format('YYYY-MM-DD') : '')}
                    style={{ width: '100%' }}
                    disabled={invoice.status === 'SENT'}
                  />
                </div>
              </Col>
              
              <Col span={5}>
                <Text type="secondary">Due Date</Text>
                <div style={{ marginTop: 4 }}>
                  <DatePicker
                    value={dueDate ? dayjs(dueDate) : null}
                    onChange={(date) => setDueDate(date ? date.format('YYYY-MM-DD') : '')}
                    style={{ width: '100%' }}
                    disabled={invoice.status === 'SENT'}
                  />
                </div>
              </Col>
              
              <Col span={8}>
                <Text type="secondary">Reference</Text>
                <div><Text>{invoice.reference}</Text></div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PendingInvoiceEditor;
